/**
 * AgroTech — Fields  (Step 1 of the 9-step intelligence loop)
 *
 * Manages field boundaries with full AgroTech agronomic metadata.
 * AgroTech-specific fields (crop type, variety, planting date, irrigation type)
 * are stored in the `metadata` JSONB column of satellite_boundaries until the
 * dedicated DB columns are migrated (see add-agrotech-fields.sql).
 *
 * Features:
 *  - Split list / map view
 *  - Draw polygon on Leaflet map → metadata form
 *  - GeoJSON file import
 *  - Edit / delete with confirmation
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { MapContainer, TileLayer, FeatureGroup, Polygon, Popup, useMap } from 'react-leaflet';
import { EditControl } from 'react-leaflet-draw';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet-draw/dist/leaflet.draw.css';
import {
  Plus, List, Map, Upload, Download, Pencil, Trash2,
  Leaf, Droplets, Calendar, Ruler, Activity,
} from 'lucide-react';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Input from '../../components/common/Input';
import Spinner from '../../components/common/Spinner';
import Alert from '../../components/common/Alert';
import { satelliteBoundariesApi } from '../../services/api';
import { useOrgSettings } from '../../hooks/useOrgSettings';

// Fix Leaflet default icons in Vite builds
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl:       'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl:     'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// ─── Types ────────────────────────────────────────────────────────────────────

interface AgroMeta extends Record<string, unknown> {
  crop_type?: string;
  crop_variety?: string;
  planting_date?: string;
  irrigation_type?: string;
}

interface Field {
  id: string;
  name: string;
  description?: string;
  geometry: { type: 'Polygon'; coordinates: number[][][] };
  area_hectares?: number;
  monitoring_enabled: boolean;
  monitoring_frequency: string;
  tags?: string[];
  metadata?: AgroMeta & Record<string, unknown>;
  created_at: string;
}

interface FieldFormState {
  name: string;
  description: string;
  crop_type: string;
  crop_variety: string;
  planting_date: string;
  irrigation_type: string;
  monitoring_enabled: boolean;
  monitoring_frequency: string;
}

const EMPTY_FORM: FieldFormState = {
  name: '',
  description: '',
  crop_type: '',
  crop_variety: '',
  planting_date: '',
  irrigation_type: '',
  monitoring_enabled: true,
  monitoring_frequency: 'weekly',
};

const CROP_TYPES = [
  'Tea', 'Coffee', 'Oil Palm', 'Kiwi', 'Avocado', 'Wheat', 'Rice',
  'Sugarcane', 'Vineyard', 'Maize', 'Cotton', 'Other',
];

const IRRIGATION_TYPES = [
  { value: 'none',      label: 'None / Rainfed' },
  { value: 'drip',      label: 'Drip' },
  { value: 'sprinkler', label: 'Sprinkler' },
  { value: 'flood',     label: 'Flood' },
  { value: 'canal',     label: 'Canal' },
  { value: 'rainfed',   label: 'Rainfed' },
];

const MONITORING_FREQUENCIES = [
  { value: 'daily',     label: 'Daily' },
  { value: 'weekly',    label: 'Weekly (recommended)' },
  { value: 'monthly',   label: 'Monthly' },
  { value: 'on_demand', label: 'On Demand' },
];

const SELECT_CLS =
  'w-full px-3 py-2 border-2 border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm focus:outline-none focus:border-primary-500 dark:focus:border-primary-400';

// Fits map to all field polygons
const FitBounds: React.FC<{ fields: Field[] }> = ({ fields }) => {
  const map = useMap();
  useEffect(() => {
    if (!fields.length) return;
    const coords = fields.flatMap((f) =>
      f.geometry.coordinates[0].map((c) => [c[1], c[0]] as [number, number])
    );
    if (coords.length) map.fitBounds(L.latLngBounds(coords), { padding: [50, 50] });
  }, [fields, map]);
  return null;
};

// Crop badge colour
function cropColour(crop: string): string {
  const map: Record<string, string> = {
    Tea: '#166534', Coffee: '#92400e', 'Oil Palm': '#7c3aed', Kiwi: '#65a30d',
    Avocado: '#15803d', Wheat: '#d97706', Rice: '#0369a1', Sugarcane: '#b45309',
    Vineyard: '#7e22ce', Maize: '#ca8a04', Cotton: '#6b7280',
  };
  return map[crop] || '#374151';
}

// ─── Main Component ───────────────────────────────────────────────────────────

export const FieldsPage: React.FC = () => {
  const { uom } = useOrgSettings();
  const [fields, setFields]         = useState<Field[]>([]);
  const [stats, setStats]           = useState<any>(null);
  const [loading, setLoading]       = useState(true);
  const [saving, setSaving]         = useState(false);
  const [error, setError]           = useState<string | null>(null);
  const [success, setSuccess]       = useState<string | null>(null);
  const [viewMode, setViewMode]     = useState<'list' | 'map'>('list');
  const [uploading, setUploading]   = useState(false);
  const fileInputRef                = useRef<HTMLInputElement>(null);

  // Form / modal state
  const [showForm, setShowForm]     = useState(false);
  const [editingId, setEditingId]   = useState<string | null>(null);
  const [form, setForm]             = useState<FieldFormState>(EMPTY_FORM);
  const [drawnLayer, setDrawnLayer] = useState<any>(null);
  const [pendingDraw, setPendingDraw] = useState(false);

  // ── Data loading ────────────────────────────────────────────────────────────
  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [res, statsRes] = await Promise.all([
        satelliteBoundariesApi.getAll(),
        satelliteBoundariesApi.getStats(),
      ]);
      setFields(res.data?.boundaries ?? []);
      setStats(statsRes.data?.stats ?? null);
    } catch (e: any) {
      setError(e?.response?.data?.message ?? 'Failed to load fields');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // ── Form helpers ────────────────────────────────────────────────────────────
  const openCreate = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setDrawnLayer(null);
    setPendingDraw(false);
    setShowForm(true);
    setViewMode('map');
  };

  const openEdit = (field: Field) => {
    setEditingId(field.id);
    setForm({
      name:                 field.name,
      description:          field.description ?? '',
      crop_type:            field.metadata?.crop_type ?? '',
      crop_variety:         field.metadata?.crop_variety ?? '',
      planting_date:        field.metadata?.planting_date ?? '',
      irrigation_type:      field.metadata?.irrigation_type ?? '',
      monitoring_enabled:   field.monitoring_enabled,
      monitoring_frequency: field.monitoring_frequency,
    });
    setDrawnLayer(null);
    setPendingDraw(false);
    setShowForm(true);
    setViewMode('list');
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingId(null);
    setForm(EMPTY_FORM);
    setDrawnLayer(null);
    setPendingDraw(false);
  };

  const setF = (k: keyof FieldFormState) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => setForm((p) => ({ ...p, [k]: e.target.value }));

  // ── Save (create or update) ─────────────────────────────────────────────────
  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim()) { setError('Field name is required'); return; }
    if (!editingId && !drawnLayer) { setError('Please draw the field boundary on the map first'); return; }

    setSaving(true);
    setError(null);
    try {
      const agro: AgroMeta = {
        crop_type:      form.crop_type || undefined,
        crop_variety:   form.crop_variety || undefined,
        planting_date:  form.planting_date || undefined,
        irrigation_type: form.irrigation_type || undefined,
      };

      if (editingId) {
        await satelliteBoundariesApi.update(editingId, {
          name:                 form.name,
          description:          form.description || undefined,
          monitoring_enabled:   form.monitoring_enabled,
          monitoring_frequency: form.monitoring_frequency,
          metadata:             agro,
          ...(drawnLayer ? { geometry: drawnLayer.toGeoJSON().geometry } : {}),
        });
        setSuccess('Field updated');
      } else {
        await satelliteBoundariesApi.create({
          name:                 form.name,
          description:          form.description || undefined,
          geometry:             drawnLayer.toGeoJSON().geometry,
          monitoring_enabled:   form.monitoring_enabled,
          monitoring_frequency: form.monitoring_frequency,
          tags:                 ['agrotech'],
          metadata:             agro,
        });
        setSuccess(form.monitoring_enabled
          ? 'Field created — satellite monitoring initialised'
          : 'Field created — monitoring is off');
      }
      closeForm();
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message ?? 'Failed to save field');
    } finally {
      setSaving(false);
    }
  };

  // ── Delete ─────────────────────────────────────────────────────────────────
  const handleDelete = async (id: string, name: string) => {
    if (!confirm(`Delete field "${name}"? This also removes all satellite imagery and anomaly data for this field.`)) return;
    try {
      await satelliteBoundariesApi.deleteById(id);
      setSuccess('Field deleted');
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message ?? 'Failed to delete field');
    }
  };

  // ── File import ────────────────────────────────────────────────────────────
  const handleFileImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true); setError(null);
    try {
      const geoJSON = JSON.parse(await file.text());
      await satelliteBoundariesApi.importGeoJSON(geoJSON);
      setSuccess(`Imported from ${file.name}`);
      await load();
    } catch (err: any) {
      setError(err?.response?.data?.message ?? 'Import failed — ensure the file is valid GeoJSON');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // ── GeoJSON export ─────────────────────────────────────────────────────────
  const handleExport = async () => {
    try {
      const res = await satelliteBoundariesApi.exportGeoJSON();
      const blob = new Blob([JSON.stringify(res.data.geoJSON, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = 'agrotech-fields.geojson';
      a.click(); URL.revokeObjectURL(url);
    } catch (e: any) {
      setError('Export failed');
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-6">

      {/* ── Header ── */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <Leaf className="w-7 h-7 text-green-600" /> Fields
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Define field boundaries and agronomic metadata — Step 1 of the AgroTech intelligence loop
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" size="sm" onClick={handleExport}>
            <Download className="w-4 h-4 mr-1" /> Export GeoJSON
          </Button>
          <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()} loading={uploading}>
            <Upload className="w-4 h-4 mr-1" /> Import GeoJSON
          </Button>
          <input ref={fileInputRef} type="file" accept=".geojson,.json" className="hidden" onChange={handleFileImport} />
          <Button onClick={openCreate}>
            <Plus className="w-4 h-4 mr-1" /> New Field
          </Button>
        </div>
      </div>

      {/* ── Alerts ── */}
      {error   && <Alert type="error"   onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert type="success" onClose={() => setSuccess(null)}>{success}</Alert>}

      {/* ── Stats row ── */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {[
            { label: 'Total Fields',   value: stats.total_boundaries   ?? 0, icon: Leaf,     colour: 'text-green-600' },
            { label: 'Active Monitoring', value: stats.active_boundaries ?? 0, icon: Activity, colour: 'text-blue-600' },
            { label: `Total Area (${uom.area_unit_label})`, value: stats.total_area_hectares != null ? Number(stats.total_area_hectares).toFixed(1) : '—', icon: Ruler, colour: 'text-amber-600' },
            { label: `Avg Area (${uom.area_unit_label})`,   value: stats.avg_area_hectares  != null ? Number(stats.avg_area_hectares).toFixed(1)  : '—', icon: Ruler, colour: 'text-purple-600' },
          ].map(({ label, value, icon: Icon, colour }) => (
            <Card key={label} padding="sm">
              <div className="flex items-center gap-3">
                <Icon className={`w-5 h-5 ${colour} flex-shrink-0`} />
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">{label}</p>
                  <p className="text-xl font-bold text-gray-900 dark:text-white">{value}</p>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* ── View toggle ── */}
      {!showForm && (
        <div className="flex gap-2">
          <Button variant={viewMode === 'list' ? 'primary' : 'outline'} size="sm" onClick={() => setViewMode('list')}>
            <List className="w-4 h-4 mr-1" /> List
          </Button>
          <Button variant={viewMode === 'map' ? 'primary' : 'outline'} size="sm" onClick={() => setViewMode('map')}>
            <Map className="w-4 h-4 mr-1" /> Map
          </Button>
        </div>
      )}

      {/* ── Create / Edit form + optional map draw ── */}
      {showForm && (
        <Card>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-5">
            {editingId ? 'Edit Field' : 'New Field'}
          </h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

            {/* Left: metadata form */}
            <form onSubmit={handleSave} className="space-y-4">

              <Input label="Field Name" required value={form.name} onChange={setF('name')} placeholder="East Block Tea Garden" />
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Description</label>
                <textarea rows={2} value={form.description} onChange={setF('description')}
                  placeholder="Optional notes…"
                  className="w-full px-3 py-2 border-2 border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm focus:outline-none focus:border-primary-500 resize-none" />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Crop Type</label>
                  <select value={form.crop_type} onChange={setF('crop_type')} className={SELECT_CLS}>
                    <option value="">Select crop…</option>
                    {CROP_TYPES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <Input label="Crop Variety / Clone" value={form.crop_variety} onChange={setF('crop_variety')} placeholder="e.g. TV1, Arabica" />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    <Calendar className="w-4 h-4 inline mr-1" />Planting / Est. Date
                  </label>
                  <input type="date" value={form.planting_date} onChange={setF('planting_date')}
                    className={SELECT_CLS} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    <Droplets className="w-4 h-4 inline mr-1" />Irrigation Type
                  </label>
                  <select value={form.irrigation_type} onChange={setF('irrigation_type')} className={SELECT_CLS}>
                    <option value="">Select…</option>
                    {IRRIGATION_TYPES.map((i) => <option key={i.value} value={i.value}>{i.label}</option>)}
                  </select>
                </div>
              </div>

              {/* Monitoring on/off + cadence */}
              <div className="space-y-3 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Satellite Monitoring</p>
                    <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                      {form.monitoring_enabled ? 'Automated satellite passes will analyse this field' : 'Field will not be included in automated runs'}
                    </p>
                  </div>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={form.monitoring_enabled}
                    onClick={() => setForm((p) => ({ ...p, monitoring_enabled: !p.monitoring_enabled }))}
                    className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${form.monitoring_enabled ? 'bg-green-500' : 'bg-gray-300 dark:bg-gray-600'}`}
                  >
                    <span
                      className={`inline-block h-5 w-5 rounded-full bg-white shadow transform transition-transform duration-200 ${form.monitoring_enabled ? 'translate-x-5' : 'translate-x-0'}`}
                    />
                  </button>
                </div>
                {form.monitoring_enabled && (
                  <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Monitoring Cadence</label>
                    <select value={form.monitoring_frequency} onChange={setF('monitoring_frequency')} className={SELECT_CLS}>
                      {MONITORING_FREQUENCIES.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
                    </select>
                  </div>
                )}
              </div>

              {!editingId && !drawnLayer && (
                <div className="rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 p-3 text-sm text-amber-700 dark:text-amber-300">
                  👉 Draw the field boundary on the map →  then click <strong>Save Field</strong>
                </div>
              )}
              {!editingId && drawnLayer && (
                <div className="rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 p-3 text-sm text-green-700 dark:text-green-300">
                  ✓ Boundary drawn — fill in the metadata above and save
                </div>
              )}

              <div className="flex gap-3 pt-2">
                <Button type="submit" loading={saving}>
                  {editingId ? 'Update Field' : 'Save Field'}
                </Button>
                <Button type="button" variant="outline" onClick={closeForm} disabled={saving}>Cancel</Button>
              </div>
            </form>

            {/* Right: Leaflet draw map */}
            <div className="rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700" style={{ height: 420 }}>
              <MapContainer
                center={[22.5937, 78.9629]}
                zoom={5}
                style={{ height: '100%', width: '100%' }}
              >
                <TileLayer
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  attribution='© <a href="https://openstreetmap.org">OpenStreetMap</a>'
                />
                {/* existing polygons as context */}
                {fields.map((f) => (
                  <Polygon
                    key={f.id}
                    positions={f.geometry.coordinates[0].map((c) => [c[1], c[0]] as [number, number])}
                    pathOptions={{ color: '#059669', weight: 2, fillOpacity: 0.15 }}
                  />
                ))}
                <FeatureGroup>
                  <EditControl
                    position="topright"
                    onCreated={(e: any) => { setDrawnLayer(e.layer); setPendingDraw(false); }}
                    draw={{ rectangle: false, circle: false, circlemarker: false, marker: false, polyline: false, polygon: true }}
                    edit={{ edit: false, remove: false }}
                  />
                </FeatureGroup>
              </MapContainer>
            </div>
          </div>
        </Card>
      )}

      {/* ── List view ── */}
      {!showForm && viewMode === 'list' && (
        <Card padding="none">
          {loading ? (
            <div className="flex justify-center py-16"><Spinner size="lg" /></div>
          ) : fields.length === 0 ? (
            <div className="text-center py-16">
              <Leaf className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
              <p className="text-gray-500 dark:text-gray-400 font-medium">No fields defined yet</p>
              <p className="text-sm text-gray-400 mt-1 mb-4">Click <strong>New Field</strong> to draw your first field boundary</p>
              <Button onClick={openCreate}><Plus className="w-4 h-4 mr-1" /> New Field</Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                  <tr>
                    {['Name', 'Crop', `Area (${uom.area_unit_label})`, 'Irrigation', 'Monitoring', 'Created', ''].map((h) => (
                      <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                  {fields.map((f) => {
                    const crop = f.metadata?.crop_type;
                    const irrigation = IRRIGATION_TYPES.find((i) => i.value === f.metadata?.irrigation_type)?.label;
                    return (
                      <tr key={f.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors">
                        <td className="px-4 py-3">
                          <p className="font-medium text-gray-900 dark:text-white">{f.name}</p>
                          {f.description && <p className="text-xs text-gray-400 mt-0.5 line-clamp-1">{f.description}</p>}
                          {f.metadata?.crop_variety && <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{f.metadata.crop_variety}</p>}
                        </td>
                        <td className="px-4 py-3">
                          {crop ? (
                            <span
                              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium text-white"
                              style={{ backgroundColor: cropColour(crop as string) }}
                            >
                              <Leaf className="w-3 h-3" />{crop}
                            </span>
                          ) : <span className="text-xs text-gray-400">—</span>}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                          {f.area_hectares != null ? Number(f.area_hectares).toFixed(2) : '—'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                          {irrigation ?? (f.metadata?.irrigation_type ? f.metadata.irrigation_type : '—')}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${f.monitoring_enabled ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400'}`}>
                            <Activity className="w-3 h-3" />
                            {f.monitoring_enabled ? f.monitoring_frequency : 'Off'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-xs text-gray-400 whitespace-nowrap">
                          {new Date(f.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <button onClick={() => openEdit(f)} title="Edit field" className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 hover:text-gray-700 dark:hover:text-white transition-colors">
                              <Pencil className="w-4 h-4" />
                            </button>
                            <button onClick={() => handleDelete(f.id, f.name)} title="Delete field" className="p-1.5 rounded hover:bg-red-50 dark:hover:bg-red-900/20 text-gray-400 hover:text-red-600 transition-colors">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {/* ── Map view ── */}
      {!showForm && viewMode === 'map' && (
        <Card padding="none">
          <div style={{ height: 560 }} className="rounded-lg overflow-hidden">
            <MapContainer center={[22.5937, 78.9629]} zoom={5} style={{ height: '100%', width: '100%' }}>
              <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution='© <a href="https://openstreetmap.org">OpenStreetMap</a>'
              />
              {fields.length > 0 && <FitBounds fields={fields} />}
              {fields.map((f) => {
                const crop = f.metadata?.crop_type as string | undefined;
                return (
                  <Polygon
                    key={f.id}
                    positions={f.geometry.coordinates[0].map((c) => [c[1], c[0]] as [number, number])}
                    pathOptions={{ color: cropColour(crop ?? ''), weight: 2, fillOpacity: 0.25 }}
                  >
                    <Popup>
                      <div className="space-y-1 text-sm min-w-[160px]">
                        <p className="font-semibold">{f.name}</p>
                        {crop && <p className="text-xs text-gray-600">🌿 {crop}{f.metadata?.crop_variety ? ` — ${f.metadata.crop_variety}` : ''}</p>}
                        {f.area_hectares != null && <p className="text-xs text-gray-600">📐 {Number(f.area_hectares).toFixed(2)} {uom.area_unit_label}</p>}
                        {f.metadata?.irrigation_type && <p className="text-xs text-gray-600">💧 {f.metadata.irrigation_type}</p>}
                        <p className="text-xs text-gray-600">
                          {f.monitoring_enabled ? `📡 Monitoring: ${f.monitoring_frequency}` : '📡 Monitoring: Off'}
                        </p>
                        <div className="flex gap-2 pt-2">
                          <button onClick={() => openEdit(f)} className="text-xs text-blue-600 underline">Edit</button>
                          <button onClick={() => handleDelete(f.id, f.name)} className="text-xs text-red-600 underline">Delete</button>
                        </div>
                      </div>
                    </Popup>
                  </Polygon>
                );
              })}
              {loading && (
                <div className="absolute inset-0 flex items-center justify-center bg-white/50 z-[1000]">
                  <Spinner />
                </div>
              )}
            </MapContainer>
          </div>
        </Card>
      )}
    </div>
  );
};

export default FieldsPage;

// Made with Bob
